import React from 'react'
import { createFocusTrap } from 'focus-trap'

/** Ref target must be a DOM element (child should use forwardRef to a host node). */
const trapContainerFromRef = (refTarget) =>
  refTarget instanceof Element ? refTarget : null

const DEFAULT_FOCUS_TRAP_PROPS = {
  active: true,
  paused: false,
  deactivateOnUnmount: true,
  focusTrapOptions: {},
  _createFocusTrap: createFocusTrap,
}

const withFocusTrapDefaults = (props) => ({
  ...DEFAULT_FOCUS_TRAP_PROPS,
  ...props,
})

class FocusTrap extends React.Component {
  constructor(props) {
    super(props)

    if (typeof document !== 'undefined') {
      this.previouslyFocusedElement = document.activeElement
    }
  }

  componentDidMount() {
    const props = withFocusTrapDefaults(this.props)
    // We need to hijack the returnFocusOnDeactivate option,
    // because React can move focus into the element before we arrived at
    // this lifecycle hook (e.g. with autoFocus inputs). So the component
    // captures the previouslyFocusedElement in componentWillMount,
    // then (optionally) returns focus to it in componentWillUnmount.
    const specifiedFocusTrapOptions = props.focusTrapOptions
    const tailoredFocusTrapOptions = {
      returnFocusOnDeactivate: false,
    }
    for (const optionName in specifiedFocusTrapOptions) {
      if (!specifiedFocusTrapOptions.hasOwnProperty(optionName)) continue
      if (optionName === 'returnFocusOnDeactivate') continue
      tailoredFocusTrapOptions[optionName] =
        specifiedFocusTrapOptions[optionName]
    }

    const focusTrapElementDOMNode = trapContainerFromRef(this.focusTrapElement)
    if (!focusTrapElementDOMNode) {
      throw new Error(
        'FocusTrap: child ref must resolve to a DOM element. Forward ref to a host component (e.g. styled() or native element).',
      )
    }

    // focus-trap >=6 throws if the container has zero tabbable nodes and no fallbackFocus
    // (v4 allowed activate in that state). Default the trap root as fallback when unset.
    if (
      focusTrapElementDOMNode &&
      !Object.prototype.hasOwnProperty.call(
        specifiedFocusTrapOptions,
        'fallbackFocus',
      )
    ) {
      tailoredFocusTrapOptions.fallbackFocus = () => focusTrapElementDOMNode
      if (!focusTrapElementDOMNode.hasAttribute('tabindex')) {
        focusTrapElementDOMNode.setAttribute('tabindex', '-1')
        this._fallbackTabIndexAdded = true
      }
    }

    this.focusTrap = props._createFocusTrap(
      focusTrapElementDOMNode,
      tailoredFocusTrapOptions,
    )
    if (props.active) {
      this.focusTrap.activate()
    }
    if (props.paused) {
      this.focusTrap.pause()
    }
  }

  componentDidUpdate(prevProps) {
    const prev = withFocusTrapDefaults(prevProps)
    const next = withFocusTrapDefaults(this.props)
    if (prev.active && !next.active) {
      const { returnFocusOnDeactivate } = next.focusTrapOptions
      const returnFocus = returnFocusOnDeactivate || false
      const config = { returnFocus }
      this.focusTrap.deactivate(config)
    } else if (!prev.active && next.active) {
      this.focusTrap.activate()
    }

    if (prev.paused && !next.paused) {
      this.focusTrap.unpause()
    } else if (!prev.paused && next.paused) {
      this.focusTrap.pause()
    }
  }

  componentWillUnmount() {
    const { deactivateOnUnmount, focusTrapOptions } = withFocusTrapDefaults(
      this.props,
    )
    if (this._fallbackTabIndexAdded) {
      const node = trapContainerFromRef(this.focusTrapElement)
      if (node && node.getAttribute('tabindex') === '-1') {
        node.removeAttribute('tabindex')
      }
    }
    if (deactivateOnUnmount) this.focusTrap.deactivate()
    if (
      focusTrapOptions.returnFocusOnDeactivate !== false &&
      this.previouslyFocusedElement &&
      this.previouslyFocusedElement.focus
    ) {
      this.previouslyFocusedElement.focus()
    }
  }

  setFocusTrapElement = (element) => {
    this.focusTrapElement = element
  }

  render() {
    const child = React.Children.only(this.props.children)

    const composedRefCallback = (element) => {
      this.setFocusTrapElement(element)
      const { ref } = child
      if (typeof ref === 'function') {
        ref(element)
      } else if (ref != null && typeof ref === 'object') {
        ref.current = element
      }
    }

    const childWithRef = React.cloneElement(child, { ref: composedRefCallback })

    return childWithRef
  }
}

export default FocusTrap
